/**
 * Sound.
 *
 * The interface has no colour, so sound carries the feedback that colour
 * usually would: a passed objective, a failed check, a level cleared. Every
 * cue is synthesised with the Web Audio API rather than loaded from a file,
 * which keeps the product asset-free and lets the cues stay short, dry and
 * quiet. Nothing here ever plays a melody; these are instrument sounds, not
 * music, and the whole set can be muted.
 *
 * Browsers refuse to start an AudioContext before a user gesture, so the
 * context is created lazily on the first cue and resumed if suspended.
 */

const MUTE_KEY = 'engineeros.muted';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function ensure(): AudioContext | null {
  if (muted) return null;
  try {
    if (!ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
      master = ctx.createGain();
      master.gain.value = 0.18;
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

interface ToneOptions {
  freq: number;
  /** Seconds. */
  dur?: number;
  type?: OscillatorType;
  /** Peak gain, 0 to 1, before the master gain. */
  gain?: number;
  /** Seconds from now. */
  delay?: number;
  /** Slide to this frequency across the note. */
  glideTo?: number;
}

function tone({ freq, dur = 0.09, type = 'sine', gain = 0.5, delay = 0, glideTo }: ToneOptions): void {
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const env = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, glideTo), t0 + dur);

  // A short percussive envelope. The 4 ms attack keeps it from clicking.
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(gain, t0 + 0.004);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  osc.connect(env);
  env.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Filtered noise burst, for the dry mechanical cues. */
function noise(dur = 0.05, gain = 0.25, freq = 1800): void {
  const c = ensure();
  if (!c || !master) return;
  const frames = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, frames, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = 0.8;
  const env = c.createGain();
  env.gain.value = gain;
  src.connect(filter);
  filter.connect(env);
  env.connect(master);
  src.start();
}

export const sound = {
  get muted(): boolean {
    return muted;
  },

  setMuted(next: boolean): void {
    muted = next;
    try {
      localStorage.setItem(MUTE_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    if (next && ctx) void ctx.suspend();
    if (!next) ensure();
  },

  /** A single objective flipped to passed. Called once per newly passed check. */
  objective(index = 0): void {
    // Each additional objective in the same check lands a tone higher, so a
    // run of four passing at once reads as an ascending figure. No delay of
    // its own: the caller fires each note on the beat its mark snaps
    // (motion.css §5, 140 ms apart), and a second stagger here would put
    // every note after the first behind its mark.
    tone({ freq: 660 * Math.pow(1.122, index), dur: 0.07, type: 'triangle', gain: 0.32 });
  },

  /** The check ran and the level is not solved yet. Dry, low, not punishing. */
  fail(): void {
    tone({ freq: 150, dur: 0.13, type: 'sine', gain: 0.4, glideTo: 110 });
    noise(0.04, 0.1, 700);
  },

  /** Level cleared. A short rising figure, three notes, then a fifth above. */
  clear(): void {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => tone({ freq: f, dur: 0.42, type: 'triangle', gain: 0.34, delay: i * 0.085 }));
  },

  /** One star landing in the clear overlay. Index 0 to 2. */
  star(index: number): void {
    tone({ freq: 880 * Math.pow(1.26, index), dur: 0.2, type: 'sine', gain: 0.3 });
    noise(0.03, 0.08, 3200);
  },

  /** A hint rung was revealed. Deliberately plain: help is not a reward. */
  hint(): void {
    tone({ freq: 392, dur: 0.1, type: 'sine', gain: 0.24 });
  },

  /** The workspace was rebuilt from the checkpoint. */
  reset(): void {
    tone({ freq: 300, dur: 0.16, type: 'sine', gain: 0.28, glideTo: 180 });
    noise(0.05, 0.12, 1000);
  },

  /** A level became available on the ledger. */
  unlock(): void {
    tone({ freq: 587.33, dur: 0.16, type: 'triangle', gain: 0.3 });
    tone({ freq: 880, dur: 0.22, type: 'triangle', gain: 0.24, delay: 0.07 });
  },

  /** A file was saved from the editor. */
  save(): void {
    noise(0.025, 0.14, 2400);
    tone({ freq: 740, dur: 0.05, type: 'sine', gain: 0.18 });
  },

  /** Generic soft click for buttons that change something. */
  click(): void {
    noise(0.018, 0.1, 2000);
  },

  /**
   * A message from someone else landed while the chat drawer was shut. One
   * dry tick, quieter and shorter than a click: it reports, it does not ask.
   * The hook that calls it allows at most one every two seconds, so a busy
   * room is a murmur behind the terminal rather than a rattle.
   */
  message(): void {
    noise(0.012, 0.05, 2600);
  },
};
